/**
 * <Commodities> — 금 · WTI 유가
 * 인플레/지정학 핵심 매크로 지표.
 * 데이터: GET /api/ticker/market/commodities → { gold: {...}, wti: {...} }
 */
"use client";

import { useEffect, useRef, useState } from "react";
import type { WidgetMeta, WidgetProps } from "../types";

interface Quote {
  symbol: string;
  label: string;
  price: number | null;
  changePercent: number | null;
  change: number | null;
}

interface CommoditiesData {
  gold: Quote | null;
  wti: Quote | null;
}

const REFRESH_MS = 5 * 60 * 1000;

function fmtPct(v: number | null) {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(2)}%`;
}
function fmtPrice(v: number | null) {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Row({ q }: { q: Quote | null }) {
  const pct = q?.changePercent ?? null;
  const up = pct != null && pct > 0;
  const down = pct != null && pct < 0;
  return (
    <div className="dw-fx-row">
      <div className="dw-fx-label">{q?.label || "—"}</div>
      <div className="dw-fx-price">{fmtPrice(q?.price ?? null)}</div>
      <div className={`dw-fx-pct ${up ? "up" : down ? "down" : ""}`}>{fmtPct(pct)}</div>
    </div>
  );
}

export function Commodities(_props: WidgetProps) {
  const [data, setData] = useState<CommoditiesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/commodities", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) { setError("로드 실패"); setLoading(false); }
          return;
        }
        const d = await res.json();
        if (mountedRef.current) { setData(d); setError(null); setLoading(false); }
      } catch {
        if (mountedRef.current) { setError("네트워크 오류"); setLoading(false); }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, []);

  return (
    <div className="dw-body dw-fx">
      <div className="dw-head"><span className="dw-title">금 · 원유</span></div>
      {error ? (
        <div className="dw-fx-error">{error}</div>
      ) : loading && !data ? (
        <div className="dw-fx-empty">로딩 중…</div>
      ) : (
        <div className="dw-fx-list">
          <Row q={data?.gold ?? null} />
          <Row q={data?.wti ?? null} />
        </div>
      )}
    </div>
  );
}

export const commoditiesMeta: WidgetMeta = {
  type: "commodities",
  name: "금·원유",
  description: "금 (GC=F) + WTI 원유 (CL=F) · 매크로 인플레 지표",
  icon: "🛢️",
  category: "market",
  defaultSize: { w: 3, h: 3 },
  availableSizes: [
    { w: 3, h: 3 },
    { w: 4, h: 3 },
    { w: 3, h: 4 },
  ],
  minW: 2,
  maxW: 6,
  minH: 2,
  maxH: 5,
  component: Commodities,
};
